import React, { FC, useContext, useRef } from 'react'
import { Popup } from '../../catalog/Popup'
import { SuccessSwap, Swap } from '../../../logic/swaps/Swap'
import styled from 'styled-components'
import metamask from '../../../assets/images/metamask.svg'
import CurrencyLogo from '../../CurrencyLogo'
import { coinImage } from '../../../assets/coins/coins'
import { ArrowDownFixed } from '../../catalog/ArrowDownFixed'
import SwapQuotation from '../SwapQuotation'
import { PVSwapLine, PVSwapQuantity, PVTokenSymbol } from './PreSwapPopup'
import { fromWei } from '../../../utils/static-web3'
import { addToWallet } from '../../../api/web3/metamask-api'
import { EmptyLine } from '../../catalog/EmptyLine'
import ValidationButton from '../../catalog/ValidationButton'
import { ViewState } from '../../pools/deposit/DepositState'
import {
  isCoinbaseInjected,
  isMetamaskInjected,
  isProbablyBrave,
} from '../../connection/provider'
import { InjectedWindow } from '../../../utils/types'
import { Web3Context } from '../../../hooks/web3'

declare const window: InjectedWindow

interface Props {
  swap: Swap & SuccessSwap
  state: ViewState
  onExit: VoidFunction
}

export const PostSwapPopup: FC<Props> = (props: Props) => {
  const { web3 } = useContext(Web3Context)

  const { onExit, swap } = props
  const { tokenIn, tokenOut, inAmountWei, realOutAmountWei } = swap

  if (!inAmountWei) {
    console.warn('No inAmountWei in SuccessSwap', swap)
  }
  if (!realOutAmountWei) {
    console.warn('No realOutAmountWei in SuccessSwap', swap)
  }

  if (!inAmountWei || !realOutAmountWei || !tokenIn || !tokenOut) {
    // security : trying to close the popup silently
    try {
      onExit()
    } catch (err) {
      console.error('Trying to close the popup silently but failed', err)
    }
    return <div />
  }

  const inAmount = fromWei(inAmountWei!, tokenIn.decimals).toPrecision(7)
  const outAmount = fromWei(realOutAmountWei!, tokenOut.decimals).toPrecision(7)
  const showAddToMetamask =
    !!window.ethereum && !isCoinbaseInjected() && !isProbablyBrave()
  isMetamaskInjected()
  const showAddToWallet =
    !!window.ethereum &&
    (!isMetamaskInjected() || isProbablyBrave()) &&
    !isCoinbaseInjected()

  const explorerUrl = swap.getExplorerUrl()
  const ref = useRef()
  return (
    <PopupWrapper className={'popup-wrapper'}>
      <Popup outsideRef={ref} onExit={onExit} title={'Success'}>
        <SectionTitle className={'pt20'}>Exact input:</SectionTitle>
        <PVSwapLine>
          <div className={'align-center'}>
            <CurrencyLogo src={coinImage(tokenIn.symbol)!} />
            <PVTokenSymbol>{tokenIn.symbol}</PVTokenSymbol>
          </div>
          <PVSwapQuantity>{inAmount}</PVSwapQuantity>
        </PVSwapLine>
        <ArrowDownFixed className={'pt20'} />
        <SectionTitle className={'pt20'}>Exact output:</SectionTitle>
        <PVSwapLine>
          <div className={'align-center'}>
            <CurrencyLogo src={coinImage(tokenOut.symbol)!} />
            <PVTokenSymbol>{tokenOut.symbol}</PVTokenSymbol>
          </div>
          <PVSwapQuantity>{outAmount}</PVSwapQuantity>
        </PVSwapLine>
        <SwapQuotation
          inCurrency={tokenIn}
          outCurrency={tokenOut}
          inQuantity={inAmount}
          outAmountWei={swap.expectedOutAmountWei}
        />
        <a
          href={explorerUrl}
          className={'link pt20 small-text'}
          target={'_blank'}
        >
          Transaction on explorer
        </a>
        <EmptyLine />
        {showAddToMetamask && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <AddToButton onClick={() => addToWallet(web3!, tokenOut, true)}>
              <img src={metamask} />{' '}
              <span>Add {tokenOut.symbol} to metamask</span>
            </AddToButton>
          </div>
        )}
        {showAddToWallet && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <AddToButton onClick={() => addToWallet(web3!, tokenOut, true)}>
              <span>Add {tokenOut.symbol} to wallet</span>
            </AddToButton>
          </div>
        )}
        <EmptyLine />
        <ValidationButton
          isReady={true}
          onClick={onExit}
          data-cy={'swap-button'}
        >
          {'Done'}
        </ValidationButton>
      </Popup>
    </PopupWrapper>
  )
}

export const AddToButton = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  cursor: pointer;
  pointer-events: auto;
  border-radius: 15px;
  font-size: 1.15rem;
  padding: 5px 12px;
  color: ${({ theme }) => theme.addToButtonColor};
  background-color: ${({ theme }) => theme.addToButtonBackgroundColor};

  &:hover {
    cursor: pointer;
    opacity: 0.85;
  }

  img {
    width: 27px;
    padding: 3px;
  }
`

// TODO: include this in the Popup component
const PopupWrapper = styled.div`
  width: 100%;
  min-width: 300px;
  font-size: 2rem;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  justify-content: center;
`

export const SectionTitle = styled.div`
  font-family: Montserrat-Light;
  text-align: right;
  width: 100%;
  padding-bottom: 0.75rem;
  font-size: 1.25rem;
`
